import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  Typography,
  FormGroupContainer,
  FormLabelStyled,
  FormControlStyled,
  FormTextStyled,
} from '../styled'
import {
  countryDialOptions,
  joinPhone,
  resolveCountryIso2,
  searchCountryDialOptions,
  splitPhone,
  DEFAULT_COUNTRY_ISO2,
} from '../../utilities/phone'

/**
 * Phone field with a country-code picker.
 *
 * The form value is the full E.164 number (`+201001234567`), so submit handlers and
 * Yup schemas keep seeing a single `phone` string. The picked country lives in
 * component state instead of the form, because a dial code does not always reverse
 * to one country — `+1` covers the US, Canada and ~20 Caribbean nations — so the
 * applicant's actual choice would otherwise be lost on the next render.
 */
export default function PhoneInput({
  name,
  label = '',
  placeholder = '',
  helperText = '',
  required = false,
  defaultCountry = DEFAULT_COUNTRY_ISO2,
  ...rest
}) {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <PhoneFieldControl
          field={field}
          fieldState={fieldState}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          required={required}
          defaultCountry={defaultCountry}
          {...rest}
        />
      )}
    />
  )
}

function PhoneFieldControl({
  field,
  fieldState,
  label = '',
  placeholder = '',
  helperText = '',
  required = false,
  defaultCountry = DEFAULT_COUNTRY_ISO2,
  ...rest
}) {
  const initial = splitPhone(field.value, resolveCountryIso2(defaultCountry))
  const [countryIso2, setCountryIso2] = React.useState(initial.countryIso2)
  const [nationalNumber, setNationalNumber] = React.useState(
    initial.nationalNumber,
  )
  const [lastFormValue, setLastFormValue] = React.useState(field.value)

  if (field.value !== lastFormValue) {
    setLastFormValue(field.value)
    const formValue = field.value || ''
    if (joinPhone(countryIso2, nationalNumber) !== formValue) {
      const next = splitPhone(formValue, countryIso2)
      setCountryIso2(next.countryIso2)
      setNationalNumber(next.nationalNumber)
    }
  }

  const handleCountryChange = (nextCountry) => {
    setCountryIso2(nextCountry)
    field.onChange(joinPhone(nextCountry, nationalNumber))
  }

  const handleNumberChange = (event) => {
    const nextNumber = event.target.value
    setNationalNumber(nextNumber)
    field.onChange(joinPhone(countryIso2, nextNumber))
  }

  return (
    <FormGroupContainer>
      {label ? (
        <FormLabelStyled>
          <Typography size="12px">
            {label}
            {required && <span className="text-danger">*</span>}
          </Typography>
        </FormLabelStyled>
      ) : null}
      <div className="d-flex flex-nowrap">
        <CountryDialSelect
          label={label}
          value={countryIso2}
          onChange={handleCountryChange}
          isInvalid={!!fieldState.error}
        />
        <FormControlStyled
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={nationalNumber}
          onChange={handleNumberChange}
          onBlur={field.onBlur}
          placeholder={placeholder}
          isInvalid={!!fieldState.error}
          {...rest}
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            marginLeft: '-1px',
            ...rest.style,
          }}
        />
      </div>
      <FormTextStyled isError={!!fieldState.error}>
        {fieldState.error ? fieldState.error.message : helperText}
      </FormTextStyled>
    </FormGroupContainer>
  )
}

/**
 * Country picker with a search box.
 *
 */
function CountryDialSelect({ label, value, onChange, disabled, isInvalid }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const options = React.useMemo(() => searchCountryDialOptions(query), [query])
  const selected = countryDialOptions.find((option) => option.value === value)

  const close = () => {
    setIsOpen(false)
    setQuery('')
  }

  const pick = (iso2) => {
    close()
    onChange(iso2)
  }

  return (
    <Dropdown
      show={isOpen}
      autoClose="outside"
      onToggle={(next) => (next ? setIsOpen(true) : close())}
      style={{ flex: '0 0 auto', width: '7.5rem' }}
    >
      <Dropdown.Toggle
        variant="outline-secondary"
        disabled={disabled}
        aria-label={`${label} country code`}
        className={`w-100 d-flex align-items-center justify-content-between ${
          isInvalid ? 'border-danger' : ''
        }`}
        style={{
          fontSize: '12px',
          fontWeight: 500,
          height: '36px',
          padding: '0.375rem 0.5rem',
          borderTopLeftRadius: '6px',
          borderBottomLeftRadius: '6px',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        {selected ? selected.label : '—'}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ width: '18rem', padding: 0 }}>
        <div className="p-2 border-bottom">
          <FormControlStyled
            autoFocus
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('general.search')}
            style={{ fontSize: '12px' }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && options.length) {
                event.preventDefault()
                pick(options[0].value)
              }
            }}
          />
        </div>
        <div style={{ maxHeight: '15rem', overflowY: 'auto' }}>
          {options.length ? (
            options.map((option) => (
              <Dropdown.Item
                key={option.value}
                active={option.value === value}
                onClick={() => pick(option.value)}
                style={{ fontSize: '12px' }}
              >
                <span className="me-2">{option.flag}</span>
                {option.name}
                <span className="text-muted ms-1">+{option.dialCode}</span>
              </Dropdown.Item>
            ))
          ) : (
            <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
              {t('general.noResults')}
            </div>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

CountryDialSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isInvalid: PropTypes.bool,
}

const fieldPropType = PropTypes.shape({
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
})

const fieldStatePropType = PropTypes.shape({
  error: PropTypes.shape({ message: PropTypes.string }),
})

PhoneFieldControl.propTypes = {
  field: fieldPropType.isRequired,
  fieldState: fieldStatePropType.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  defaultCountry: PropTypes.string,
}

PhoneInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  defaultCountry: PropTypes.string,
}
